import { createFileRoute, redirect } from "@tanstack/react-router";

/**
 * ``/auth/callback`` — post-OAuth landing strip (May 25, 2026).
 *
 * Every connector OAuth flow (Gmail, Drive, Calendar, Slack, …) ends with
 * the BE issuing a 302 to ``{frontend_origin}/auth/callback?status=connected
 * &provider=<slug>&...``. V1 has a Next page at this path that bootstraps
 * the session cookie via ``/auth/me``; V2's session is already Bearer-based
 * and in secure storage at this point, so the only job here is **routing**:
 *
 *   status=connected → /workspace/integrations (the hub the FE expects to
 *                       show the "X collegata" toast on)
 *   status=error     → /workspace/integrations with the error params so
 *                       the hub can surface a clear toast
 *   return_to_query_id → /workspace/conversations (chat replay flow —
 *                          ``_authed/index.tsx`` and the chat slice consume
 *                          ``return_to_query_id`` for OAuth step-up)
 *
 * Implemented via TanStack ``beforeLoad`` (server-side equivalent before
 * the component mounts) so the redirect happens BEFORE any rendering —
 * no spinner flash, no double-mount.
 *
 * Why a top-level route and not just an intercept on ``_authed.route``:
 * the URL is ``/auth/callback`` (NOT under ``_authed``), so the
 * authenticated-shell intercept never fires. This file makes the path
 * an explicit, well-known endpoint that's robust against future shell
 * refactors.
 */
export const Route = createFileRoute("/auth/callback")({
  beforeLoad: ({ search }) => {
    const s = search as {
      access_mode?: string;
      connection_id?: string;
      status?: string;
      provider?: string;
      page?: string;
      error?: string;
      return_to_query_id?: string;
      capability?: string;
      email?: string;
      account?: string;
      workspace?: string;
      org_id?: string;
      company_id?: string;
      company_count?: string;
      count?: string;
      bot_id?: string;
    };

    // OAuth step-up: user kicked the flow off from a chat turn and wants
    // to replay it post-consent. Bounce into the conversations route with
    // the original query id so the chat slice can re-issue the question.
    if (s.return_to_query_id && s.access_mode !== "send_only") {
      throw redirect({
        to: "/workspace/conversations",
        search: {
          status: s.status,
          provider: s.provider,
          return_to_query_id: s.return_to_query_id,
          ...(s.capability ? { capability: s.capability } : {}),
        },
        replace: true,
      });
    }

    // Default: land on the integrations hub. The hub's ``index.tsx`` and
    // ``$capability.tsx`` already consume ``status`` + ``provider`` to
    // invalidate the per-capability cache and toast "X collegata".
    // ``error`` rides through identically so the hub can render a precise
    // failure toast.
    throw redirect({
      to: "/integrations",
      search: {
        ...(s.status ? { status: s.status } : {}),
        ...(s.provider ? { provider: s.provider } : {}),
        ...(s.error ? { error: s.error } : {}),
        ...(s.access_mode === "send_only" ? { access_mode: "send_only", connection_id: s.connection_id ?? "" } : {}),
      },
      replace: true,
    });
  },
});
